import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { COLORS } from "./constants";

// Build a CanvasTexture, register cleanup so GPU memory is freed on unmount.
function useDisposableCanvasTexture(builder, deps) {
  const tex = useMemo(() => {
    const t = builder();
    return t;
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (tex) {
        if (tex.image && typeof document !== "undefined") {
          // help GC drop the offscreen canvas
          tex.image = null;
        }
        tex.dispose();
      }
    };
  }, [tex]);

  return tex;
}

export const useNoiseTexture = (size = 256, color1 = COLORS.grass, color2 = COLORS.grassDark) =>
  useDisposableCanvasTexture(() => {
    const c = document.createElement("canvas");
    c.width = c.height = size;
    const ctx = c.getContext("2d");
    ctx.fillStyle = color1;
    ctx.fillRect(0, 0, size, size);
    for (let i = 0; i < size * size * 0.18; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? color2 : color1;
      ctx.globalAlpha = 0.18 + Math.random() * 0.25;
      ctx.fillRect(Math.random() * size, Math.random() * size, 2, 2);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(8, 8);
    t.anisotropy = 4;
    return t;
  }, [size, color1, color2]);

export const useStoneTexture = () =>
  useDisposableCanvasTexture(() => {
    const size = 256;
    const c = document.createElement("canvas");
    c.width = c.height = size;
    const ctx = c.getContext("2d");
    ctx.fillStyle = COLORS.stone;
    ctx.fillRect(0, 0, size, size);
    const tile = 32;
    for (let y = 0; y < size; y += tile) {
      for (let x = 0; x < size; x += tile) {
        const offset = (y / tile) % 2 === 0 ? 0 : tile / 2;
        const tx = (x + offset) % size;
        const shade = 0.85 + Math.random() * 0.3;
        ctx.fillStyle = `rgba(${Math.floor(141 * shade)}, ${Math.floor(133 * shade)}, ${Math.floor(
          118 * shade
        )}, 1)`;
        ctx.fillRect(tx, y, tile - 2, tile - 2);
        ctx.strokeStyle = COLORS.stoneDark;
        ctx.lineWidth = 1;
        ctx.strokeRect(tx, y, tile - 2, tile - 2);
      }
    }
    for (let i = 0; i < 400; i++) {
      ctx.fillStyle = `rgba(60,55,45,${Math.random() * 0.4})`;
      ctx.fillRect(Math.random() * size, Math.random() * size, 1, 1);
    }
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 4;
    return t;
  }, []);

export const useBannerTexture = (text = "TAB") =>
  useDisposableCanvasTexture(() => {
    const w = 512;
    const h = 768;
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, COLORS.navyMid);
    grad.addColorStop(1, COLORS.navy);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = COLORS.gold;
    ctx.lineWidth = 16;
    ctx.strokeRect(24, 24, w - 48, h - 48);
    ctx.beginPath();
    ctx.arc(w / 2, 200, 90, 0, Math.PI * 2);
    ctx.strokeStyle = COLORS.gold;
    ctx.lineWidth = 8;
    ctx.stroke();
    ctx.fillStyle = COLORS.goldBright;
    ctx.font = "bold 90px Georgia, serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("T", w / 2, 200);
    ctx.fillStyle = COLORS.goldBright;
    ctx.font = "bold 150px Georgia, serif";
    ctx.fillText(text, w / 2, 420);
    ctx.fillStyle = COLORS.ivory;
    ctx.font = "italic 36px Georgia, serif";
    ctx.fillText("THE ACADEMIC", w / 2, 540);
    ctx.fillText("BOARDROOM", w / 2, 590);
    ctx.strokeStyle = COLORS.gold;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(120, 650);
    ctx.lineTo(w - 120, 650);
    ctx.stroke();
    ctx.fillStyle = COLORS.gold;
    ctx.font = "28px Georgia, serif";
    ctx.fillText("EST · MMXXVI", w / 2, 690);
    const t = new THREE.CanvasTexture(c);
    t.anisotropy = 8;
    return t;
  }, [text]);
